// @ts-check
/**
 * **動きの道具**（Issue #540。索引 #539）。`script` 層（コードで書く演出）が使う純関数。
 *
 * **時計を持たない**（`purity.test.js`）—— どれも「帯の頭からの経過」を受け取って値を返すだけ。
 * gsap は使わない: タイムラインを seek するより、**時刻 → 状態の純関数**で書く方が
 * シーク（前へも後ろへも）と書き出し（1 フレームずつ撮る）で必ず同じ絵になる。
 *
 * three は知らない（`core/` は three を import しない）。
 */

export const clamp01 = (v) => Math.min(1, Math.max(0, v));
export const lerp = (a, b, t) => a + (b - a) * t;

/**
 * **イージング**（`core/effect::EASES` の追加分）。
 * 行き過ぎて戻る（`back_out`）・弾む（`elastic_out`）は宣言のキーフレームからも使える。
 */
export const EXTRA_EASES = Object.freeze({
  back_out: (t) => { const s = 1.70158; return 1 + (s + 1) * (t - 1) ** 3 + s * (t - 1) ** 2; },
  elastic_out: (t) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    return 2 ** (-10 * t) * Math.sin(((t * 10 - 0.75) * 2 * Math.PI) / 3) + 1;
  },
  expo_out: (t) => (t >= 1 ? 1 : 1 - 2 ** (-10 * t)),
  power4_out: (t) => 1 - (1 - t) ** 4,
  power2_in: (t) => t * t,
  power3_in: (t) => t * t * t,
});

/** 区間 [a, b] の中での進み 0〜1（外は端に張り付く） */
export const seg = (t, a, b) => (b > a ? clamp01((t - a) / (b - a)) : (t >= b ? 1 : 0));

/**
 * 区間の進みにイージングを掛ける（`ease` は名前か関数）。
 * @param {number} t
 * @param {number} a
 * @param {number} b
 * @param {string | ((t: number) => number)} [ease]
 */
export function tween(t, a, b, ease = 'linear') {
  const p = seg(t, a, b);
  const fn = typeof ease === 'function' ? ease : (EASE_FNS[ease] ?? EASE_FNS.linear);
  return fn(p);
}

/**
 * **入って・保って・抜ける** 0→1→0（光・文字の出入り）。
 * @returns {number}
 */
export function pulse(t, { at = 0, inMs = 120, hold = 0, outMs = 200, ease = 'ease_out' } = {}) {
  if (t < at) return 0;
  if (t < at + inMs) return tween(t, at, at + inMs, ease);
  if (t < at + inMs + hold) return 1;
  return 1 - tween(t, at + inMs + hold, at + inMs + hold + outMs, 'ease_in');
}

/** i 番目の開始時刻（`each` ずつずらす） */
export const stagger = (i, start, each) => start + i * each;

/**
 * **決定論的な乱数**（同じ種なら同じ列。粒子・揺らぎに使う。`Math.random` は使わない）。
 * FNV-1a → xorshift32。
 * @param {string|number} seedText
 * @returns {() => number} 0 以上 1 未満
 */
export function seeded(seedText) {
  let h = 2166136261;
  for (const ch of String(seedText)) { h ^= ch.codePointAt(0) ?? 0; h = Math.imul(h, 16777619); }
  let x = h >>> 0 || 1;
  return () => { x ^= x << 13; x >>>= 0; x ^= x >>> 17; x ^= x << 5; x >>>= 0; return x / 4294967296; };
}

/**
 * **なめらかなノイズ**（-1〜1。整数の格子に乱数を置いて cosine で繋ぐ）。
 * カメラの揺れ・炎の揺らぎに使う。同じ `(seed, x)` なら同じ値。
 */
export function noise1(seed, x) {
  const i = Math.floor(x);
  const f = x - i;
  const at = (n) => seeded(`${seed}:${n}`)() * 2 - 1;
  const k = (1 - Math.cos(f * Math.PI)) / 2;
  return lerp(at(i), at(i + 1), k);
}

/** 減衰する揺れ（`amp` から `decayMs` で 0 へ。`freq` は 1 秒あたりの揺れ） */
export function shake(seed, ms, { amp = 12, freq = 18, decayMs = 400 } = {}) {
  if (!(amp > 0) || ms < 0) return [0, 0];
  const k = decayMs > 0 ? Math.max(0, 1 - ms / decayMs) : 1;
  if (k <= 0) return [0, 0];
  const x = (ms / 1000) * freq;
  return [noise1(`${seed}:x`, x) * amp * k, noise1(`${seed}:y`, x) * amp * k];
}

// `core/effect::EASES` に混ぜるものの一覧は index.js。ここでは tween のために自前で引く
const BASE = {
  linear: (t) => t,
  hold: () => 0,
  ease_in: (t) => t * t,
  ease_out: (t) => 1 - (1 - t) ** 2,
  ease_in_out: (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2),
};
const EASE_FNS = { ...BASE, ...EXTRA_EASES };
